/**
 * Makes drafted text safe to send as a plain-GSM-7 SMS.
 *
 * Why this exists: one non-GSM-7 character (a "→" was the live-QA one) flips a
 * whole text to UCS-2, which fits 67 characters per segment instead of 153. The
 * drafted change notice went out at 5 segments, over the 4-segment limit the
 * broadcast API enforces, so the host's own suggested text could not be sent.
 *
 * This is deliberately NOT a GSM-7 table. The arithmetic that bills lives in
 * OrchestratorAPI's `SmsSegments`, and Studio asks the API to count a text
 * (lib/smsEstimate.ts) rather than copy its rules. GSM-7's accent coverage is
 * arbitrary (é à ñ ö are free; á í ó ú ç are not) and a second table would drift,
 * so instead of knowing which characters GSM-7 allows, this keeps only a strict
 * subset of ASCII that is single-septet in every GSM-7 variant, and rewrites
 * everything else. Accented letters are folded to their base letter (the
 * drafted text is a suggestion the host can edit, and "Almoco" is a better
 * outcome than a doubled bill), which also keeps accents that GSM-7 would have
 * allowed folded — a small, predictable loss rather than a per-letter lookup.
 */

/**
 * Printable ASCII that is one septet in GSM-7: letters, digits, and simple
 * punctuation. "<" and ">" are in GSM-7's basic set, and they MUST be kept: the
 * arrow fold below writes "->" and "<-", so dropping them made the function
 * non-idempotent ("→" → "->" → "-?" on a second pass, and Studio runs it at
 * both draft and send time).
 */
const KEEP = /[A-Za-z0-9 \n.,:;!?'"()/%&+=*#@$_<>-]/;

/** Typographic characters people paste, and the plain ASCII that says the same thing. */
const PUNCTUATION: Record<string, string> = {
  '‘': "'", '’': "'", '‚': "'", '′': "'", '`': "'",
  '“': '"', '”': '"', '„': '"', '″': '"',
  '–': '-', '—': '-', '−': '-', '‐': '-', '‑': '-', '~': '-',
  '→': '->', '←': '<-',
  '…': '...',
  '•': '-', '·': '-',
  ' ': ' ', ' ': ' ', ' ': ' ', ' ': ' ', ' ': ' ', ' ': ' ',
  '[': '(', '{': '(', ']': ')', '}': ')',
  '|': '/', '\\': '/', '^': '',
  '€': 'EUR',
};

/** Letters with no Unicode decomposition, so folding by combining mark misses them. */
const LETTERS: Record<string, string> = {
  'ø': 'o', 'Ø': 'O', 'æ': 'ae', 'Æ': 'AE', 'œ': 'oe', 'Œ': 'OE',
  'ß': 'ss', 'ł': 'l', 'Ł': 'L', 'đ': 'd', 'Đ': 'D',
  'ð': 'd', 'Ð': 'D', 'þ': 'th', 'Þ': 'Th',
};

export interface SmsSafeResult {
  text: string;
  /** True when a character had no sensible ASCII equivalent and became "?". */
  lossy: boolean;
}

/** Combining marks left behind by NFD: what remains after "ç" → "c" + U+0327. */
const COMBINING_MARKS = /[̀-ͯ]/g;

export function toSmsSafe(input: string): SmsSafeResult {
  let lossy = false;
  let out = '';
  // NFD first so "Almoço" becomes "Almoco" + a combining cedilla to drop, and
  // so a pre-composed "é" and a decomposed "é" take the same path.
  const decomposed = input.normalize('NFD').replace(COMBINING_MARKS, '');
  for (const char of decomposed) {
    if (KEEP.test(char)) {
      out += char;
    } else if (PUNCTUATION[char] !== undefined) {
      out += PUNCTUATION[char];
    } else if (LETTERS[char] !== undefined) {
      out += LETTERS[char];
    } else if (char === '\r' || char === '\t') {
      out += ' ';
    } else {
      out += '?';
      lossy = true;
    }
  }
  return { text: out.replace(/[ ]{2,}/g, ' '), lossy };
}
